import CircularProgress from '@material-ui/core/CircularProgress';
import Tooltip from '@material-ui/core/Tooltip';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import GetAppIcon from '@material-ui/icons/GetApp';
import { useHistory, useParams } from 'react-router-dom';
import moment from 'moment';
import React from 'react';
import styled from 'styled-components';
import { useSetModalTitle } from '../../../hooks/modal/useSetModalTitle';
import {
  DeviceDataExport,
  isDeviceDataExportPreparing,
  useDeviceDataExports,
} from '../../../hooks/useDeviceDataExports';
import Button from '../../bits/Button.jsx';
import Tile from '../../bits/Tile.jsx';
import TileActionRow from '../../bits/TileActionRow.jsx';
import Loading from '../Loading.jsx';

const HeaderActions = styled.div`
  margin-bottom: 32px;
`;

const ExportListHeading = styled.h2`
  color: #4a4a4a;
  font-size: 1.25em;
  font-weight: 600;
  margin: 0 0 12px;
`;

const EmptyState = styled.div`
  align-items: center;
  background: #f7f9fc;
  border: 1px dashed #c7d5e3;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  padding: 32px 24px;
  text-align: center;
`;

const EmptyStateIcon = styled(DescriptionOutlinedIcon)`
  color: #8aafd1;
  font-size: 48px !important;
  margin-bottom: 12px;
`;

const EmptyStateTitle = styled.h3`
  color: #4a4a4a;
  font-size: 1em;
  font-weight: 600;
  margin-bottom: 4px;
`;

const EmptyStateDescription = styled.p`
  color: #7a7a7a;
  font-size: 0.9em;
  line-height: 1.5;
  margin: 0;
  max-width: 360px;
`;

const DownloadLink = styled.a`
  align-items: center;
  color: #4a8bcc;
  display: inline-flex;
  height: 24px;
  justify-content: center;
  width: 24px;

  &:hover {
    color: #3576b7;
  }
`;

const FailedIcon = styled(ErrorOutlineIcon)`
  color: #cd3d64;
`;

const StatusIcon = ({ dataExport }: { dataExport: DeviceDataExport }) => {
  if (isDeviceDataExportPreparing(dataExport)) {
    return (
      <Tooltip title="Preparing" enterTouchDelay={0}>
        <span aria-label="Preparing export" role="status">
          <CircularProgress size={24} />
        </span>
      </Tooltip>
    );
  }

  if (dataExport.status === 'failed') {
    return (
      <Tooltip title="Failed" enterTouchDelay={0}>
        <span aria-label="Export failed" role="status">
          <FailedIcon />
        </span>
      </Tooltip>
    );
  }

  if (dataExport.downloadUrl) {
    return (
      <Tooltip title="Download CSV" enterTouchDelay={0}>
        <DownloadLink
          aria-label="Download CSV"
          href={dataExport.downloadUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          <GetAppIcon />
        </DownloadLink>
      </Tooltip>
    );
  }

  return <span>Expired</span>;
};

const ExportListItem = ({ dataExport }: { dataExport: DeviceDataExport }) => {
  const datapointLabel = dataExport.rowCount === 1 ? 'datapoint' : 'datapoints';
  const rowCount = dataExport.rowCount === null
    ? ''
    : ` · ${dataExport.rowCount.toLocaleString()} ${datapointLabel}`;
  const range = `${moment(dataExport.startAt).format('MMM D, YYYY')} – `
    + `${moment(dataExport.endAt).subtract(1, 'millisecond').format('MMM D, YYYY')}${rowCount}`;

  return (
    <TileActionRow
      label={moment(dataExport.createdAt).format('MMM D, YYYY h:mm A')}
      description={range}
      action={<StatusIcon dataExport={dataExport} />}
    />
  );
};

export const DeviceDataExportsRoute: React.FC = () => {
  const { id: deviceId } = useParams<{ id: string }>();
  const history = useHistory();
  const { data: dataExports, isLoading } = useDeviceDataExports(deviceId);
  const exportInProgress = dataExports?.some(isDeviceDataExportPreparing) ?? false;

  useSetModalTitle('Data Exports');

  return (
    <>
      <HeaderActions>
        <Button
          color="primary"
          disabled={isLoading || exportInProgress}
          onClick={() => history.push(
            `/m/devices/${deviceId}/data-exports/new`,
            { origin: 'data_exports' },
          )}
        >
          Create Export
        </Button>
      </HeaderActions>

      {isLoading && <Loading />}

      {!isLoading && dataExports && (
        <>
          <ExportListHeading>Recent exports</ExportListHeading>
          {dataExports.length === 0 ? (
            <EmptyState>
              <EmptyStateIcon aria-hidden="true" />
              <EmptyStateTitle>No exports yet</EmptyStateTitle>
              <EmptyStateDescription>
                Create an export to download this device’s location history as a CSV file.
              </EmptyStateDescription>
            </EmptyState>
          ) : (
            <Tile>
              {dataExports.map(dataExport => (
                <ExportListItem key={dataExport.id} dataExport={dataExport} />
              ))}
            </Tile>
          )}
        </>
      )}
    </>
  );
};
